/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { TrackedArray } from 'tracked-built-ins';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  compact,
  filterBy,
  mapBy,
  resolved,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class BookingChip extends Component {
  @action
  setBooking(value) {
    this.booking = value;
  }

  // Services
  @service
  heroApiAllocations;

  @service
  session;

  @service
  store;

  @service
  toaster;

  chipData = null;

  @alias('chipData.allocation')
  allocation;

  @alias('chipData.bookings')
  bookings;

  @alias('chipData.slot')
  slot;

  @tracked
  booking = null;
  allBookings = null;
  @tracked editable = true;
  @tracked restrictedMode = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get useSecondaryContacts() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  @tracked
  caregivers = null;
  @tracked
  editBookingDialogOpen = false;
  @tracked
  editBookingDialogOpening = false;
  @tracked
  restrictedMessageDialogOpen = false;
  @tracked
  bookingChangeInProgress = false;

  // An admin can click to edit a booking if the bookings are editable (based on event status)
  // A teacher with event.staffBookingManagement true can click to edit any booking
  // A teacher with event.staffBookingManagement false can click to edit only booked bookings
  // (teachers can only see editable events)
  // Plain getter, not @computed: `bookings` is TrackedArray-backed
  // (chipData.bookings, from buildChipDataMap), and a classic macro/
  // computed doesn't reliably observe a TrackedArray's autotracked reads
  // the way it does a real EmberArray's.
  get clickAction() {
    if (!this.editable) {
      return this.noop;
    }
    if (this.restrictedMode && isEmpty(this.bookings)) {
      return this.restrictedMessage;
    }
    return this.editBooking;
  }

  // firstBooking replaces the template's old this.bookings.firstObject
  // reads (trap #1 - .firstObject silently resolves to undefined on a
  // plain/TrackedArray, since neither has a real .firstObject property).
  get firstBooking() {
    return this.bookings[0];
  }

  @computed(
    'booking.{student.id,caregiver.id,onlineMeetingURL,hasDirtyAttributes}',
  )
  get saveBookingButtonDisabled() {
    let onlineMeetingUrlInvalid = !this.onlineMeetingUrlValidations[0].validate(
      this.get('booking.onlineMeetingURL'),
    );
    return (
      isEmpty(this.get('booking.student.id')) ||
      isEmpty(this.get('booking.caregiver.id')) ||
      onlineMeetingUrlInvalid ||
      !this.get('booking.hasDirtyAttributes')
    );
  }

  onlineMeetingUrlValidations = Object.freeze([
    {
      message: 'URL must have http:// or https://',
      validate: (val) => {
        return (
          isEmpty(val) ||
          val.startsWith('http://') ||
          val.startsWith('https://')
        );
      },
    },
  ]);

  updateCaregivers(reloadBookings) {
    // Get all primary contacts for student
    let potentialCaregivers = compact(
      mapBy(
        this.get('booking.student').get('learner.primaryContacts'),
        'caregiver',
      ),
    );

    if (this.useSecondaryContacts) {
      // Add secondary contacts who have Hero access, if applicable.
      let secondaryContacts = compact(
        mapBy(
          this.get('booking.student').get('learner.secondaryContacts'),
          'caregiver',
        ),
      );

      if (!isEmpty(secondaryContacts)) {
        potentialCaregivers.push(...secondaryContacts);
      }
    }

    let maybeUpdate = Promise.resolve(this.allBookings);
    if (reloadBookings) {
      maybeUpdate = this.allBookings.update();
    }
    maybeUpdate.then((bookings) => {
      // Filter out any caregivers with bookings in the same slot
      let filteredCaregivers = potentialCaregivers.filter((caregiver) => {
        let caregiverId = caregiver.get('id');
        let caregiversOtherBookings = bookings.filter(
          (b) => b.caregiver_id === caregiverId,
        );
        return !caregiversOtherBookings.some((caregiversOtherBooking) => {
          return (
            caregiversOtherBooking.get('allocation.slot.id') ===
            this.get('slot.id')
          );
        });
      });
      this.caregivers = filteredCaregivers;
    });
  }

  @computed('allocation.{groups.content.[],bookedStudentIds.[]}')
  get students() {
    let groups = resolved(this.allocation.groups);
    return uniqBy(
      groups.reduce((students, group) => {
        students.push(
          ...mapBy(group.get('currentLearners'), 'person').filter((student) => {
            // Only one booking per student per allocation
            if (
              this.get('allocation.bookedStudentIds').includes(
                student.get('id'),
              )
            ) {
              return false;
            }
            let hasPrimaryContacts = !isEmpty(
              student.get('learner.primaryContacts'),
            );
            if (this.useSecondaryContacts) {
              // If secondary contacts are enabled, any student
              // with either primary or secondary contacts should
              // be available to select.
              return (
                hasPrimaryContacts || student.get('learner.secondaryContacts')
              );
            }
            return hasPrimaryContacts;
          }),
        );
        return students;
      }, []),
      'id',
    );
  }

  async createBooking() {
    // A belongsTo takes a record, not the async proxy.
    let { school, event } = await hash({
      school: this.allocation.schoolId,
      event: this.allocation.eventId,
    });
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    let props = {
      schoolId: school,
      eventId: event,
      allocationId: this.allocation,
      authorId: this.session.get('person'),
    };
    let booking = this.store.createRecord('bookings/booking', props);
    this.booking = booking;
    this.bookings.push(booking);
  }

  @action
  noop() {}

  @action
  restrictedMessage() {
    this.restrictedMessageDialogOpen = true;
  }

  @action
  editBooking() {
    // Check to avoid double clicks
    if (this.editBookingDialogOpening) {
      return;
    }
    this.editBookingDialogOpening = true;
    // Reload the allocation early to be sure of status
    this.allocation
      .reload()
      .then(() => {
        if (!isEmpty(this.bookings)) {
          this.bookings.forEach((booking) => booking.reload());
          this.booking = this.bookings[0];
          this.editBookingDialogOpen = true;
        } else if (this.allocation.get('blocked')) {
          this.editBookingDialogOpen = true;
        }
        // If allocation is available or reserved and not blocked, attempt to reserve it, or confirm that it is our reservation
        else if (
          this.allocation.get('available') > 0 ||
          this.allocation.get('reservedByUser')
        ) {
          return this.heroApiAllocations
            .reserve(this.allocation.get('id'))
            .then(async () => {
              // If successfully reserved, open dialog
              await this.createBooking();
              this.editBookingDialogOpen = true;
            })
            .catch(() => {
              this.toaster.error('Allocation is currently reserved!');
            });
        }
      })
      .finally(() => {
        this.editBookingDialogOpening = false;
      });
  }

  @action
  closeEditBookingDialog() {
    if (this.allocation.get('reservedByUser')) {
      // Attempt to release allocation
      this.heroApiAllocations.release(this.allocation.get('id'));
    }
    if (!isEmpty(this.bookings)) {
      // Stop changes to url, notes, student & caregiver remaining on booking
      this.bookings.forEach((booking) => rollbackRecord(booking));
      // Filter away any unsaved bookings
      this.set('bookings', new TrackedArray(filterBy(this.bookings, 'id')));
      if (!this.get('booking.id')) {
        this.booking = null;
      }
    }
    this.editBookingDialogOpen = false;
  }

  @action
  blockBooking() {
    this.bookingChangeInProgress = true;
    this.heroApiAllocations
      .block(this.allocation.get('id'))
      .then(() => {
        this.toaster.success('Booking blocked successfully!');
        this.booking.destroyRecord();
        this.booking = null;
        this.set('bookings', new TrackedArray());
        this.editBookingDialogOpen = false;
      })
      .catch(() => {
        this.toaster.error('Booking block failed!');
      })
      .finally(() => {
        this.bookingChangeInProgress = false;
      });
  }

  @action
  unblockBooking() {
    this.bookingChangeInProgress = true;
    this.heroApiAllocations
      .unblock(this.allocation.get('id'))
      .then(() => {
        this.toaster.success('Booking unblocked successfully!');
        this.editBookingDialogOpen = false;
      })
      .catch(() => {
        this.toaster.error('Booking unblock failed!');
      })
      .finally(() => {
        this.bookingChangeInProgress = false;
      });
  }

  @action
  cancelBooking() {
    this.bookingChangeInProgress = true;
    this.booking
      .destroyRecord()
      .then(() => {
        this.toaster.success('Booking cancelled successfully!');
        let pos = this.bookings.indexOf(this.booking);
        if (pos > -1) {
          this.bookings.splice(pos, 1);
        }
        this.booking = null;
        this.editBookingDialogOpen = false;
      })
      .catch(() => {
        this.toaster.error('Booking cancel failed!');
      })
      .finally(() => {
        this.bookingChangeInProgress = false;
      });
  }

  @action
  saveBooking() {
    this.bookingChangeInProgress = true;
    this.booking
      .save()
      .then(() => {
        // Undo any other changes
        this.bookings.forEach((booking) => booking.rollbackAttributes());
        this.toaster.success('Booking saved successfully!');
        this.editBookingDialogOpen = false;
      })
      .catch(() => {
        this.toaster.error('Booking save failed!');
        this.allocation.reload();
      })
      .finally(() => {
        this.bookingChangeInProgress = false;
      });
  }

  @action
  addStudent(student) {
    this.booking.set('student', student);
    this.updateCaregivers(true);
  }

  @action
  removeCaregiver() {
    this.set('booking.caregiverId', '');
    this.updateCaregivers(false);
  }

  @action
  createAdditionalBooking() {
    this.heroApiAllocations
      .reserve(this.allocation.get('id'))
      .then(() => this.createBooking())
      .catch(() => {
        this.toaster.error('Allocation is currently reserved!');
      });
  }
}
